import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Autocomplete, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import DataGridTable from "../Components/DataGridTable";
import { useDialog } from "../Components/DialogProvider";
import { useAuth } from "../hooks/AuthProvider";
import {
  createPeopleCounterDevice,
  deletePeopleCounterDevice,
  getPeopleCounterDevices,
  getPeopleCounterOrganizations,
  updatePeopleCounterDevice,
} from "../api/peopleCounterApi";
import { formatMacAddressInput, isValidMacAddress } from "../utils/peopleCounterStats";
import PeopleCounterLayout, { usePeopleCounterTranslations } from "./PeopleCounterLayout";

const EMPTY_FORM = { name: "", macAddress: "", organization: null };

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.INFO || error?.message || fallback;

export default function PeopleCounterDevices() {
  const { org, permissions } = useAuth();
  const canView = Boolean(permissions?.view_people_counter_devices);
  const canAdd = Boolean(permissions?.add_people_counter_devices);
  const canEdit = Boolean(permissions?.edit_people_counter_devices);
  const canDelete = Boolean(permissions?.delete_people_counter_devices);
  const { t } = usePeopleCounterTranslations();
  const dialog = useDialog();
  const [rows, setRows] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organizationsReady, setOrganizationsReady] = useState(false);
  const [organizationFilter, setOrganizationFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const requestSequence = useRef(0);

  const selectedOrganization = useMemo(
    () => organizations.find((item) => Number(item.id) === Number(organizationFilter)) || null,
    [organizationFilter, organizations]
  );
  const allOrganizationsOption = useMemo(
    () => ({ id: null, name: t.allOrganizations, isAll: true }),
    [t.allOrganizations]
  );
  const organizationFilterOptions = useMemo(
    () => [allOrganizationsOption, ...organizations],
    [allOrganizationsOption, organizations]
  );
  const selectedOrganizationOption = selectedOrganization || allOrganizationsOption;

  const loadOrganizations = useCallback(async () => {
    if (!canView) return;
    setOrganizationsReady(false);
    try {
      const response = await getPeopleCounterOrganizations();
      const options = Array.isArray(response.data?.data) ? response.data.data : [];
      const currentOrganization = options.find((item) => Number(item.id) === Number(org?.id));
      setOrganizations(options);
      setOrganizationFilter(currentOrganization?.id || null);
    } catch (requestError) {
      setOrganizations([]);
      setOrganizationFilter(null);
      setError(getErrorMessage(requestError, t.loadOrganizationsFailed));
    } finally {
      setOrganizationsReady(true);
    }
  }, [canView, org?.id, t.loadOrganizationsFailed]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const fetchDevices = useCallback(async () => {
    if (!canView || !organizationsReady) return;
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError("");
    try {
      const sort = sortModel?.[0] || {};
      const response = await getPeopleCounterDevices({
        page,
        pageSize,
        sortField: sort.field || "name",
        sortOrder: sort.sort || "asc",
        filterModel: JSON.stringify(filterModel || { items: [] }),
        ...(organizationFilter ? { organizationId: organizationFilter } : {}),
      });
      if (sequence !== requestSequence.current) return;
      const data = Array.isArray(response.data?.data) ? response.data.data : [];
      setRows(data);
      setRowCount(Number(response.data?.pagination?.total || 0));
    } catch (requestError) {
      if (sequence !== requestSequence.current) return;
      setRows([]);
      setRowCount(0);
      setError(getErrorMessage(requestError, t.loadDevicesFailed));
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [canView, filterModel, organizationFilter, organizationsReady, page, pageSize, sortModel, t.loadDevicesFailed]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const openCreate = () => {
    if (!canAdd) return;
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      organization: selectedOrganization || organizations[0] || null,
    });
    setFormOpen(true);
  };

  const openEdit = useCallback((row) => {
    if (!canEdit) return;
    setEditingId(row.people_counter_id);
    setForm({
      name: row.name || "",
      macAddress: row.mac_address || "",
      organization: organizations.find((item) => Number(item.id) === Number(row.organization_id)) || null,
    });
    setFormOpen(true);
  }, [canEdit, organizations]);

  const saveDevice = async () => {
    if ((editingId && !canEdit) || (!editingId && !canAdd)) return;
    const name = form.name.trim();
    if (!name) {
      await dialog.alert({ variant: "error", title: t.validationTitle, message: t.nameRequired });
      return;
    }
    if (!isValidMacAddress(form.macAddress)) {
      await dialog.alert({ variant: "error", title: t.validationTitle, message: t.macInvalid });
      return;
    }
    if (!form.organization?.id) {
      await dialog.alert({ variant: "error", title: t.validationTitle, message: t.organizationRequired });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name,
        macAddress: form.macAddress,
        organizationId: Number(form.organization.id),
      };
      if (editingId) await updatePeopleCounterDevice(editingId, payload);
      else await createPeopleCounterDevice(payload);
      setFormOpen(false);
      setForm(EMPTY_FORM);
      await dialog.alert({
        variant: "success",
        title: t.savedTitle,
        message: editingId ? t.updatedSuccessfully : t.createdSuccessfully,
      });
      await fetchDevices();
    } catch (requestError) {
      await dialog.alert({
        variant: "error",
        title: t.saveFailedTitle,
        message: getErrorMessage(requestError, t.saveFailed),
      });
    } finally {
      setSaving(false);
    }
  };

  const removeDevice = useCallback(async (row) => {
    if (!canDelete) return;
    const confirmed = await dialog.confirm({
      title: t.deleteTitle,
      message: t.deleteMessage.replace("{name}", row.name),
      confirmText: t.delete,
      cancelText: t.cancel,
    });
    if (!confirmed) return;

    try {
      await deletePeopleCounterDevice(row.people_counter_id);
      await fetchDevices();
    } catch (requestError) {
      await dialog.alert({
        variant: "error",
        title: t.deleteFailedTitle,
        message: getErrorMessage(requestError, t.deleteFailed),
      });
    }
  }, [canDelete, dialog, fetchDevices, t]);

  const columns = useMemo(() => {
    const nextColumns = [];

    if (canEdit || canDelete) {
      nextColumns.push({
        field: "actions",
        headerName: t.actions,
        type: "actions",
        width: 90,
        getActions: (params) => {
          const actions = [];
          if (canEdit) {
            actions.push(
              <GridActionsCellItem
                key="edit"
                icon={<Tooltip title={t.edit}><EditOutlinedIcon /></Tooltip>}
                label={t.edit}
                onClick={() => openEdit(params.row)}
                showInMenu
              />
            );
          }
          if (canDelete) {
            actions.push(
              <GridActionsCellItem
                key="delete"
                icon={<Tooltip title={t.delete}><DeleteOutlineIcon color="error" /></Tooltip>}
                label={t.delete}
                onClick={() => removeDevice(params.row)}
                showInMenu
              />
            );
          }
          return actions;
        },
      });
    }

    nextColumns.push(
      { field: "name", headerName: t.deviceName, minWidth: 220, flex: 1 },
      { field: "mac_address", headerName: t.macAddress, minWidth: 220, flex: 1 },
      { field: "organization_name", headerName: t.organization, minWidth: 240, flex: 1 }
    );

    return nextColumns;
  }, [canDelete, canEdit, openEdit, removeDevice, t]);

  return (
    <PeopleCounterLayout
      title={t.devicesTitle}
      description={t.devicesDescription}
      requiredPermission="view_people_counter_devices"
      actions={canAdd ? (
        <button
          type="button"
          className="devices-primary-btn people-counter-button"
          onClick={openCreate}
          disabled={!organizationsReady || !organizations.length}
        >
          <AddIcon fontSize="small" /> <span>{t.addDevice}</span>
        </button>
      ) : null}
    >
      {error ? <div className="people-counter-error">{error}</div> : null}
      <section className="devices-grid-wrapper people-counter-grid-card">
        <DataGridTable
          columns={columns}
          rows={rows}
          idcolumn="people_counter_id"
          pagingoptions={[10, 20, 50, 100]}
          sortField="name"
          sortType="asc"
          showToolbar
          tableKey="people_counter_devices"
          loading={loading}
          toolbarPrefix={(
            <Autocomplete
              className="people-counter-toolbar-control people-counter-field"
              size="small"
              options={organizationFilterOptions}
              value={selectedOrganizationOption}
              loading={!organizationsReady}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
              onChange={(_event, value) => {
                setOrganizationFilter(value?.id || null);
                setPage(1);
              }}
              renderInput={(params) => <TextField {...params} label={t.filterOrganization} />}
            />
          )}
          pagination={{
            serverSide: true,
            page,
            pageSize,
            count: rowCount,
            sortModel,
            onPageChange: setPage,
            onPageSizeChange: (value) => { setPageSize(value); setPage(1); },
            onSortModelChange: (value) => { setSortModel(value?.length ? value : []); setPage(1); },
            onFilterModelChange: (value) => { setFilterModel(value || { items: [] }); setPage(1); },
          }}
        />
      </section>

      <Dialog
        open={formOpen}
        onClose={saving ? undefined : () => setFormOpen(false)}
        fullWidth
        maxWidth="sm"
        className="people-counter-dialog"
      >
        <DialogTitle>{editingId ? t.editDevice : t.addDevice}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: "12px !important" }}>
          <TextField
            className="people-counter-field"
            autoFocus
            label={t.deviceName}
            value={form.name}
            inputProps={{ maxLength: 191 }}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          />
          <TextField
            className="people-counter-field"
            label={t.macAddress}
            value={form.macAddress}
            placeholder="AA:BB:CC:DD:EE:FF"
            inputProps={{ maxLength: 17, spellCheck: false }}
            onChange={(event) => setForm((current) => ({
              ...current,
              macAddress: formatMacAddressInput(event.target.value),
            }))}
          />
          <Autocomplete
            className="people-counter-field"
            options={organizations}
            value={form.organization}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
            onChange={(_event, value) => setForm((current) => ({ ...current, organization: value }))}
            renderInput={(params) => <TextField {...params} label={t.organization} />}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <button type="button" className="devices-secondary-btn people-counter-button" onClick={() => setFormOpen(false)} disabled={saving}>
            <span>{t.cancel}</span>
          </button>
          <button type="button" className="devices-primary-btn people-counter-button" onClick={saveDevice} disabled={saving}>
            <span>{saving ? t.saving : t.save}</span>
          </button>
        </DialogActions>
      </Dialog>
    </PeopleCounterLayout>
  );
}
